import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [userData, setUserData] = useState<any>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Ana sayfada mıyız kontrolü
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    checkUser();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        checkUser();
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserData(null);
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      setUser(user);
      
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      
      setUserData(userData);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setShowUserMenu(false);
    window.location.reload();
  };

  // Linkleri eski haline (ID'li) getirdik
  const menuItems = [
    { name: 'Ana Sayfa', href: '#home', type: 'section' },
    { name: 'Hakkımda', href: '#hakkimda', type: 'section' },
    { name: 'Ders Notları', href: '/notes', type: 'page' },
    { name: 'LGS Denemeleri', href: '/lgs-exams', type: 'page' },
    { name: 'Yapay Zeka Değerlendirme', href: '/ai-evaluation', type: 'page' },
    { name: 'İletişim', href: '#iletisim', type: 'section' },
  ];

  const roleNames: Record<string, string> = {
    student: 'Öğrenci',
    parent: 'Veli',
    teacher: 'Öğretmen',
  };

  // Navigasyon Mantığı Oluşturan Fonksiyon
  const renderMenuItem = (item: any, isMobile = false) => {
    const baseClasses = isMobile 
      ? `block py-3 px-4 rounded-lg font-medium transition-colors duration-300 ${
          isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
        }`
      : `text-sm font-medium transition-colors duration-300 hover:text-teal-500 whitespace-nowrap ${
          (isScrolled || !isHomePage) ? 'text-gray-700' : 'text-white'
        } ${location.pathname === item.href ? 'font-bold text-teal-600' : ''}`;

    // DURUM 1: Bu bir sayfa linki ise (/notes vb.) -> Her zaman Link kullan
    if (item.type === 'page') {
      return (
        <Link
          key={item.name}
          to={item.href}
          onClick={() => isMobile && setIsMobileMenuOpen(false)}
          className={baseClasses}
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          {item.name}
        </Link>
      );
    }

    // DURUM 2: Bu bir bölüm linki ise (#hakkimda vb.)
    if (item.type === 'section') {
      if (isHomePage) {
        // Ana sayfadaysak -> <a> etiketi kullan (Sayfa içi kaydırma çalışsın)
        return (
          <a
            key={item.name}
            href={item.href}
            onClick={() => isMobile && setIsMobileMenuOpen(false)}
            className={baseClasses}
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {item.name}
          </a>
        );
      } else {
        // Başka sayfadaysak -> Ana sayfaya yönlendir
        return (
          <Link
            key={item.name}
            to="/"
            onClick={() => isMobile && setIsMobileMenuOpen(false)}
            className={baseClasses}
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {item.name}
          </Link>
        );
      }
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHomePage ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-20">
            
            {/* LOGO - Her zaman ana sayfaya gider */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer group">
              <img 
                src="https://static.readdy.ai/image/13efc658f5e4badd7f709164859f9a94/a99824fa5b58eead6e6c1148f2900914.jpeg" 
                alt="Tolga AYDOS Logo" 
                className="w-12 h-12 rounded-xl object-cover group-hover:scale-110 group-hover:rotate-6 transition-all duration-500"
              />
              <div>
                <h1 className={`text-xl font-bold transition-colors duration-300 ${
                  isScrolled || !isHomePage ? 'text-gray-900' : 'text-white'
                }`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Tolga AYDOS
                </h1>
                <p className={`text-sm transition-colors duration-300 ${
                  isScrolled || !isHomePage ? 'text-teal-600' : 'text-teal-200'
                }`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Fen Bilimleri Öğretmeni
                </p>
              </div>
            </Link>

            {/* Desktop Linkleri */}
            <div className="hidden lg:flex items-center gap-8">
              {menuItems
                .filter(item => user ? !item.showWhenLoggedOut : true) // (Typo fix: showWhenLoggedOut varsa)
                .map((item) => renderMenuItem(item, false))}
            </div>

            {/* Desktop Kullanıcı Menüsü / Giriş Butonu */}
            <div className="hidden lg:flex items-center gap-4">
              {user && userData ? (
                <>
                  {userData.is_admin && (
                    <Link
                      to="/admin"
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm font-medium"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      <i className="ri-admin-line mr-2"></i>
                      Admin
                    </Link>
                  )}

                  <div className="relative">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                        (isScrolled || !isHomePage) 
                          ? 'bg-gray-100 hover:bg-gray-200' 
                          : 'bg-white/10 hover:bg-white/20 backdrop-blur-sm'
                      }`}
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-xs">
                          {userData.full_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="text-left">
                        <p className={`text-sm font-medium ${isScrolled || !isHomePage ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {userData.full_name}
                        </p>
                        <p className={`text-xs ${isScrolled || !isHomePage ? 'text-gray-500' : 'text-white/80'}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {roleNames[userData.role]}
                        </p>
                      </div>
                      <i className={`ri-arrow-down-s-line transition-transform ${showUserMenu ? 'rotate-180' : ''} ${isScrolled || !isHomePage ? 'text-gray-400' : 'text-white/80'}`}></i>
                    </button>

                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                        <Link
                          to="/dashboard"
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setShowUserMenu(false)}
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          <i className="ri-dashboard-line text-gray-400"></i>
                          Panel
                        </Link>
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setShowUserMenu(false)}
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          <i className="ri-user-line text-gray-400"></i>
                          Profil Ayarları
                        </Link>
                        <div className="h-px bg-gray-200 my-2"></div>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          <i className="ri-logout-box-line"></i>
                          Çıkış Yap
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <Link
                  to="/auth/login"
                  className="px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Giriş Yap
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-300 ${
                isScrolled || !isHomePage ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen' : 'max-h-0'
        } ${isScrolled ? 'bg-white' : 'bg-gray-900/95 backdrop-blur-lg'}`}
      >
        <div className="px-4 py-6 space-y-4">
          {user && userData && (
            <div className={`flex items-center gap-3 p-4 rounded-lg mb-4 ${
              isScrolled ? 'bg-gray-50' : 'bg-white/10'
            }`}>
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {userData.full_name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className={`text-sm font-medium ${isScrolled ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {userData.full_name}
                </p>
                <p className={`text-xs ${isScrolled ? 'text-gray-500' : 'text-white/80'}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {roleNames[userData.role]}
                </p>
              </div>
            </div>
          )}

          {/* Mobile Linkleri */}
          {menuItems
            .filter(item => user ? !item.showWhenLoggedOut : true)
            .map((item) => renderMenuItem(item, true))}
          
          {user && userData ? (
            <>
              {userData.is_admin && (
                <Link
                  to="/admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold text-center hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  <i className="ri-admin-line mr-2"></i>
                  Admin Paneli
                </Link>
              )}
              
              <Link
                to="/dashboard"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold text-center hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <i className="ri-dashboard-line mr-2"></i>
                Panelim
              </Link>

              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-3 px-4 rounded-lg font-medium transition-colors duration-300 ${
                  isScrolled 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <i className="ri-user-line mr-2"></i>
                Profil Ayarları
              </Link>

              <button
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="block w-full py-3 px-4 bg-red-500 text-white rounded-xl font-semibold text-center hover:bg-red-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                <i className="ri-logout-box-line mr-2"></i>
                Çıkış Yap
              </button>
            </>
          ) : (
            <Link
              to="/auth/login"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold text-center hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Giriş Yap
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}